import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { setOnboardingTab, setOnboardingComplete, setOnboardingRoute, setIsSynergyskySession } from '../../stores/actions/session';
import { setUseMobile } from '../../stores/actions/video';
import { setJoinRequest, loadInvitations } from '../../stores/actions/invitations';
import { replaceState } from '../../stores/actions/settings';
import Socket from '../../connection/socket';
import { ONBOARDING_ROUTES } from '../../stores/session-status';
import Icon, { ICONS, COLORS } from '../general-components/icon';
import * as Alerts from '../general-components/alert-service';
import {
	CheckJoinRequest,
	GetRequiresApproval,
	JoinRequest,
} from '../../connection/join-request';
import { GreenroomState as GS } from '../../stores/types/types';
import { getStateFromSettingsJson } from '../../utils/sync';
import ConfirmProfile from './confirm-profile';
import SetupConferenceRoom from './setup-conference-room';
import { MODAL_TEMPLATES } from '../../stores/reducers/modal';
import { setModalState } from '../../stores/actions/modal';
import DeviceSelection from './device-selection';
import { GetTokboxToken, GetSession } from '../../connection/opentok';
import NetworkTest from 'opentok-network-test-js';
import * as OT from '@opentok/client';
import { UpdateCallbackStats } from 'opentok-network-test-js/dist/NetworkTest/types/callbacks';
import { AverageStats, HasAudioVideo } from 'opentok-network-test-js/dist/NetworkTest/testQuality/types/stats';
import { IntermediateResult, passesQualityCheck, QualityResults } from '../../utils/network';
import ConnectivityCheck from './connectivity-check';
import { useMediaDevicesContext } from '../../contexts/media/check-media';
import useTempPublisher from '../../hooks/video/use-temp-publisher';
import styled from 'styled-components';
import { setShowboardExpanded } from '../../stores/actions/user-interface';
import logger from '../../utils/logger-rum';
const BRANDLIVE_OPENTOK_API_KEY = process.env.REACT_APP_OPENTOK_API_KEY ?? '';

const SYNERGYSKY_MEDIA_RETRY_INTERVAL_MS = 1000;
const SYNERGYSKY_MEDIA_MAX_RETRIES = 20;

const NavButton = styled.div`
	display: flex;
	align-items: center;
	font-size: 13px;
	font-weight: 600;
	cursor: pointer;

	svg {
		margin: 0 8px;
	}
`;

interface Props {
	// props that will be passed to this component
	socket: Socket | null;
}

const ONBOARDING_TABS = {
	INFO: 0,
	AV: 1,
	SPEED: 2,
};

const tabs = [
	'one',
	'two',
	'three',
];

const SYNERGYSKY_KEY = process.env.SYNERGYSKY_KEY ?? 'greenroom-qa';

interface PerformanceEntryWithType extends PerformanceEntry {
	type?: string;
}

export default function Onboarding(props: Props) {
	const onboardingTab = useSelector((s: GS) => s.SessionReducer.onboardingTab);
	const token = useSelector((s: GS) => s.SessionReducer.token);
	const meeting = useSelector((s: GS) => s.SessionReducer.meeting);
	const skipApproval = useSelector((s: GS) => s.SessionReducer.skipApproval);
	const watcherOnly = useSelector((s: GS) => s.SessionReducer.watcher_only);
	const companionMode = useSelector((s: GS) => s.SessionReducer.companionMode);
	const restrictedUserState = useSelector((s: GS) => s.SessionReducer.restrictedUserState);
	const liveChannels = useSelector((s: GS) => s.ContentReducer.liveChannels);
	const mainVideoStream = useSelector((s: GS) => s.VideoReducer.mainVideoStream);
	const useMobile = useSelector((s: GS) => s.VideoReducer.useMobile);
	const joinRequest = useSelector((s: GS) => s.InvitationsReducer.joinRequest);
	const entryWithoutProducer = useSelector((s: GS) => s.SettingsReducer.entryWithoutProducer);
	const askToJoin = useSelector((s: GS) => s.SettingsReducer.askToJoin);
	const limitedInput = useSelector((s: GS) => s.SessionReducer.limitedInput);
	const isSynergyskySession = useSelector((s: GS) => s.SessionReducer.isSynergyskySession);

	const { socket } = props;

	const dispatch = useDispatch();
	const { checkMediaDevices } = useMediaDevicesContext();
	const [devicesChecked, setDevicesChecked] = useState<boolean>(false);

	const [waiting, setWaiting] = useState<boolean>(false);
	const [showConferenceRoomFlow, setShowConferenceRoomFlow] = useState<boolean>(false);
	const joinRequestCheckInverval = useRef<NodeJS.Timeout | null>(null);
	const startSessionCalledRef = useRef<boolean>(false);

	const [promptForPermissions, setPromptForPermissions] = useState<boolean>(true);
	const [intermediateResults, setIntermediateResults] = useState<IntermediateResult[]>([]);
	const [qualityResults, setQualityResults] = useState<QualityResults | null>(null);
	const [speedTestStartTime, setSpeedTestStartTime] = useState<number>(Date.now());
	const [didShowDeviceSelection, setDidShowDeviceSelection] = useState<boolean>(false);
	const {publisher, videoEl} = useTempPublisher(devicesChecked && didShowDeviceSelection);

	useEffect(() => {
		if (mainVideoStream) {
			setDevicesChecked(true);
			setPromptForPermissions(false);
		}
	}, [mainVideoStream]);

	useEffect(() => {
		const navigationEntries = window.performance.getEntriesByType('navigation');
		const reload = (navigationEntries[0] as PerformanceEntryWithType)?.type === 'reload';
		const skipOnboardingOnReload = sessionStorage.getItem('skipOnboardingOnReload') === 'true';

		let hasCheckedDevices = !!mainVideoStream;
		if (!hasCheckedDevices && (reload || skipOnboardingOnReload)) {
			checkMediaDevices();
			hasCheckedDevices = true;
		}

		if (!limitedInput && (!reload || !skipOnboardingOnReload)) return;

		// limited input devices should start with the showboard expanded
		dispatch(setShowboardExpanded(true));

		if (!mainVideoStream) {
			const synergySkyObject = window.synergysky;
			if (synergySkyObject && !startSessionCalledRef.current) {
				startSessionCalledRef.current = true;
				synergySkyObject.startSession("1.0", SYNERGYSKY_KEY)
					.then(() => {
						dispatch(setIsSynergyskySession(true));
					})
					.catch((e: Error) => {
						logger.error('[SynergySky] Session setup failed:', e);
						Alerts.showAlert({
							message: 'Unable to connect to SynergySky',
							description: 'Session setup failed. Please try refreshing.',
							type: 'error',
							duration: 7000,
						});
					});
			} else if (!synergySkyObject && !hasCheckedDevices) {
				// on reload, init devices immediately to let users back in immediately
				// otherwise, permissions will be checked when onboardingTab === ONBOARDING_TABS.AV
				checkMediaDevices();
			}
		} else if (!isSynergyskySession) {
			join();
		}
	}, [limitedInput, mainVideoStream]);

	useEffect(() => {
		if (!isSynergyskySession) return;

		let retries = 0;
		let retryTimeout: NodeJS.Timeout;

		const getResultLabel = (result: string): string => {
			if (result === 'getUserMedia_unavailable') return 'Media API not yet available';
			if (result === 'permissions_off') return 'Media permissions denied';
			if (result.startsWith('error:')) return result.slice(6);
			return result;
		};

		const attemptCheckMedia = async () => {
			const result = await checkMediaDevices();

			if (result === 'ok') {
				return;
			}

			// Permanent failures — do not retry
			if (result === 'permissions_off') {
				const label = getResultLabel(result);
				logger.error(`[SynergySky] ${label} — not retrying`);
				return;
			}

			retries++;
			const reason = getResultLabel(result);
			if (retries <= SYNERGYSKY_MEDIA_MAX_RETRIES) {
				logger.warn(`[SynergySky] ${reason}, retrying (${retries}/${SYNERGYSKY_MEDIA_MAX_RETRIES})...`);
				retryTimeout = setTimeout(attemptCheckMedia, SYNERGYSKY_MEDIA_RETRY_INTERVAL_MS);
			} else {
				logger.error(`[SynergySky] ${reason} after ${SYNERGYSKY_MEDIA_MAX_RETRIES} retries`);
			}
		};

		attemptCheckMedia();

		return () => clearTimeout(retryTimeout);
	}, [isSynergyskySession]);

	useEffect(() => {
		if (!isSynergyskySession || !mainVideoStream) return;
		join(true);
	}, [mainVideoStream]);

	useEffect(() => {
		if (companionMode) {
			join();
		}
	}, [companionMode])

	function goBack() {
		if (onboardingTab > 0) {
			dispatch(setOnboardingTab(onboardingTab - 1));
		}
		if (useMobile && onboardingTab === ONBOARDING_TABS.SPEED) {
			dispatch(setUseMobile(false));
		}
	}

	async function join(isSynergysky = false) {
		try {
			if (!token || !meeting) return;

			if (!watcherOnly && !companionMode && !mainVideoStream && !restrictedUserState && !isSynergysky) {
				setWaiting(false);
				Alerts.showAlert({
					message: 'Unable to join Greenroom',
					description: 'Please set your camera and microphone permissions before entering.',
					type: 'error',
					duration: 5000,
				});
				return;
			}

			// check if this event requires approval
			const { approval_required: required } = await GetRequiresApproval(token, meeting.meeting);

			if (required) {
				// check whether or not we have a previous approval
				const { approved } = await CheckJoinRequest(token, meeting.meeting);

				// if approved, load the invitations and set onboarding to complete to load the main UI
				if (approved) {
					window.history.pushState(undefined, 'Greenroom', `/${meeting.meeting_id}`);
					dispatch(loadInvitations(token, meeting.meeting));
					dispatch(setOnboardingComplete(true));
				} else {
					// if not approved, send a join request and wait
					const { join_request: request } = await JoinRequest(token, meeting.meeting);
					dispatch(setJoinRequest(request));
				}
			} else {
				// approval not required, load invitations, set onboarding to complete to load the main UI
				window.history.pushState(undefined, 'Greenroom', `/${meeting.meeting_id}`);
				dispatch(loadInvitations(token, meeting.meeting));
				dispatch(setOnboardingComplete(true));
			}
		} catch (e) {
			logger.error(e);
			Alerts.showAlert({
				message: 'Unable to Join',
				description: 'We ran into an issue checking permissions to join the greenroom. Please try again.',
				type: 'error',
				duration: 7000,
			});
		}
	}

	function next() {
		if (watcherOnly) {
			setWaiting(true);
			join();
		} else if (onboardingTab < 2) {
			dispatch(setOnboardingTab(onboardingTab + 1));
		} else if (meeting) {
			setWaiting(true);
			join();
		} else {
			dispatch(setOnboardingRoute(ONBOARDING_ROUTES.PRESENTER_SELECT_MEETING));
		}
	}

	function shouldButtonBeDisabled(): boolean {
		return onboardingTab === ONBOARDING_TABS.SPEED
			&& !!meeting?.meeting
			&& !liveChannels?.includes(meeting?.meeting)
			&& !entryWithoutProducer;
	}

	async function checkJoinRequest(request: number) {
		if (request && token && meeting) {
			const result = await CheckJoinRequest(token, meeting.meeting);

			if (result?.approved) {
				if (joinRequestCheckInverval.current) clearInterval(joinRequestCheckInverval.current);
				window.history.pushState(undefined, 'Greenroom', `/${meeting.meeting_id}`);
				dispatch(loadInvitations(token, meeting.meeting));
				dispatch(setOnboardingComplete(true));
			} else if (!result) {
				logger.error('Join request failed, result:', result)
			}
		}
	}

	useEffect(() => {
		if (socket && skipApproval && !watcherOnly) {
			dispatch(setOnboardingTab(1));
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [socket]);

	useEffect(() => {
		// producer dropped while awaiting join request
		if (meeting && !liveChannels.includes(meeting.meeting)) {
			if (entryWithoutProducer && askToJoin) return;
			setWaiting(false);
		}
	}, [liveChannels, meeting]);

	useEffect(() => {
		// forcing the join request to poll AND wait for websocket in case the connection is spotty
		if (joinRequest) {
			joinRequestCheckInverval.current = setInterval(() => checkJoinRequest(joinRequest), 3000);
		}

		return () => {
			if (joinRequestCheckInverval.current) clearInterval(joinRequestCheckInverval.current);
		};
	}, [joinRequest]);

	useEffect(() => {
		if (meeting) {
			dispatch(replaceState(getStateFromSettingsJson(meeting.settings_json)));
		}

		navigator.permissions.query({ name: 'camera' as PermissionName }).then((perm) => {
			if (perm.state === 'granted') {
				setPromptForPermissions(false);
			}
		});
	}, []);

	useEffect(() => {
		if (!askToJoin) setWaiting(false);
	}, [askToJoin]);

	useEffect(() => {
		if (onboardingTab === ONBOARDING_TABS.AV) {
			setDidShowDeviceSelection(true);
			if (promptForPermissions) {
				dispatch(setModalState({
					modalOpen: true,
					modalTemplate: MODAL_TEMPLATES.DEVICE_PERMISSION,
				}));
			}
			checkMediaDevices().then(() => {
				setDevicesChecked(true);
				dispatch(setModalState({
					modalOpen: false,
					modalTemplate: MODAL_TEMPLATES.DEVICE_PERMISSION,
				}));
			});
		}
	}, [onboardingTab]);

	useEffect(() => {
		if (!devicesChecked) return;

		navigator.permissions.query({ name: 'camera' as PermissionName }).then((perm) => {
			if (perm.state === 'granted') {
				startConnectivityTest();
			}
		});
	}, [devicesChecked]);

	function startConnectivityTest() {
		GetSession().then((sessionResult) => {
			GetTokboxToken(sessionResult.sessionId!).then((result) => {
				checkConnectivity(sessionResult.sessionId, result.token);
			});
		});
	}

	async function checkConnectivity(sessionId: string, tokboxToken: string) {
		try {
			const sessionInfo = {
				apiKey: BRANDLIVE_OPENTOK_API_KEY,
				sessionId: sessionId,
				token: tokboxToken,
			};
			const options = { timeout: 30000 }; // 30 seconds
			// @ts-ignore
			const networkTest = new NetworkTest(OT, sessionInfo, options);

			if (networkTest) {
				const connectionResults = await networkTest.testConnectivity();
				if (connectionResults && connectionResults.success) {
					const startTime: number = Date.now();
					setSpeedTestStartTime(startTime);

					let lastTimestamp: number = startTime;
					let lastBytesSent: number  = 0;
					const qualityResults: HasAudioVideo<AverageStats> = await networkTest.testQuality((stats: UpdateCallbackStats) => {
						const elapsedSeconds = (stats.timestamp - lastTimestamp) / 1000;
						const bytesSent = stats.video?.bytesSent - lastBytesSent;
						const kbps = ((bytesSent * 8)) / elapsedSeconds / 1000;

						setIntermediateResults((prev) => [...prev, {
							timestamp: stats.timestamp,
							videoKbps: kbps,
						}]);

						lastTimestamp = stats.timestamp;
						lastBytesSent = stats.video?.bytesSent;
					});

					const video: AverageStats = qualityResults.video;
					if (video.bitrate !== undefined && video.packetLossRatio != undefined) { 
						setQualityResults({ bitrate: video.bitrate, packetLossRatio: video.packetLossRatio });
					}
				}
			}
		} catch (e) {
			logger.error(e);
			setQualityResults({ bitrate: 0, packetLossRatio: 0 });
		}
	};

	const backButton = (
		<NavButton onClick={goBack}>
			<Icon name={ICONS.ARROW_LEFT} color={COLORS.LIGHT_GRAY} size={12} />
			<span className="text-light-gray">Back</span>
		</NavButton>
	);

	if (showConferenceRoomFlow) {
		return <SetupConferenceRoom exitConferenceRoomFlow={() => setShowConferenceRoomFlow(false)} />
	}

	return (
		<div className={'onboarding-tabs onboarding-tabs-profile-update'}>
			<div className={classNames(`gradient-container-container`, {'centered': onboardingTab === ONBOARDING_TABS.SPEED})}>
				<div className='gradient-container'>
					<div className="gradient yellow" />
					<div className="gradient purple" />
				</div>
				<div className='gradient-offset'>{/*to offset the gradient to the left of center*/}</div>
			</div>
			<div className={classNames('tabs-container', tabs[onboardingTab])}>

				<div className="tab-wrapper one">
					<div className="scroll-container">
						<ConfirmProfile
							enterConferenceRoomFlow={() => setShowConferenceRoomFlow(true)}
							next={next}
							nextButtonDisabled={shouldButtonBeDisabled()} />
					</div>
				</div>
				{!watcherOnly && (
					<>
						<div className="tab-wrapper two">
							<div className="scroll-container">
								{onboardingTab === ONBOARDING_TABS.AV && (
									<DeviceSelection
										publisher={publisher}
										videoEl={videoEl}
										enterConferenceRoomFlow={() => setShowConferenceRoomFlow(true)}
										next={next}
										nextButtonDisabled={shouldButtonBeDisabled()}
									/>
								)}
							</div>
						</div>
						<div className="tab-wrapper three">
							<div className="scroll-container">
								{onboardingTab === ONBOARDING_TABS.SPEED && (
									<ConnectivityCheck
										startTime={speedTestStartTime}
										intermediateResults={intermediateResults}
										qualityResults={qualityResults}
										next={next}
										nextButtonDisabled={shouldButtonBeDisabled()}
									/>
								)}
							</div>
						</div>
					</>
				)}
			</div>

			{askToJoin && joinRequest && waiting ? (
				<span className="waiting-message">You'll join when the producer lets you in </span>
			) : (
				<>
					{shouldButtonBeDisabled() && (
						<span className="waiting-message">Hang tight, the Producer will let you in.</span>
					)}
				</>
			)}

				<div 
					style={{ display: 'flex', justifyContent: 'flex-start', visibility: skipApproval ? 'hidden' : 'initial' }}
					className='onboarding-tab-back-button'
				>
					{onboardingTab > 0 ? backButton : <div className="placeholder" />}
				</div>
		<div className="tab-navigation">
				{!watcherOnly ? (
					<div className='tab-container onboarding-tab-bottom-indicator'>
						<div className="current-tab-indicator" style={skipApproval ? { visibility: 'hidden' } : {}}>
							{useMobile && tabs.slice(0, 3).map((tn, ti) => (
								<div key={['tab', tn].join('-')} className={classNames('indicator', { active: onboardingTab === ti })}></div>
							))}
							{!useMobile && tabs.map((tn, ti) => (
								<div key={['tab', tn].join('-')} className={classNames('indicator', { active: onboardingTab === ti })}></div>
							))}
						</div>
						{<img src='/brandlive_logo.png' alt="" />}
					</div>
				) : <div></div>}
			</div>
			{onboardingTab === ONBOARDING_TABS.SPEED && (
				<div
					style={{ display: 'flex', justifyContent: 'flex-end', visibility: skipApproval ? 'hidden' : 'initial' }}
					className='onboarding-tab-skip-button'
				>
					{(!qualityResults || !passesQualityCheck(qualityResults)) && (
						<NavButton onClick={next}>
							<span className="text-dark-gray">Skip this step</span>
							<Icon name={ICONS.ARROW_RIGHT} color={COLORS.DARK_GRAY} size={12} />
						</NavButton>
					)}
				</div>
			)}
		</div>
	);
}
